/**
 * Indicatore versione app — angolo basso-destra, fixed, semitrasparente.
 *
 * Davide chiede di vedere a colpo d'occhio quale build sta girando, per
 * evitare la confusione "ho aggiornato o no?" dopo un deploy.
 * Mostra `v1.0.6 · 11/05, 14:32` con data/ora del build.
 *
 * v1.0.15: la versione viene letta LIVE da ``/health`` invece che dalla
 * costante ``__APP_VERSION__`` iniettata da Vite a build-time, cosi' il
 * badge riflette il backend in esecuzione e non il momento del build della
 * SPA. Fallback su ``__APP_VERSION__`` se il backend non risponde. In dev mode
 * (``import.meta.env.DEV``) aggiunge il chip "DEV" per distinguere a colpo
 * d'occhio l'app dev dall'app installata.
 *
 * 2026-09-11: il badge si accorge anche quando la PAGINA e' vecchia. Il
 * controllo girava una volta sola al caricamento e il poll era acceso solo in
 * dev: una scheda lasciata aperta continuava a eseguire il JavaScript del
 * giorno prima, e di un rilascio non arrivava nessun segnale — successo con il
 * tasto «Segna pagato» e l'ordine 499010, entrambi presenti sul server e
 * invisibili a schermo. Ora ``/health`` porta ``spa_build`` (il nome del
 * bundle servito, che contiene l'hash del contenuto: la versione ``1.3.1``
 * resta identica fra due immagini, il nome del file no) e lo si confronta con
 * il bundle DA CUI QUESTO MODULO E' STATO CARICATO (``import.meta.url``).
 * Diversi = c'e' una versione nuova, e il badge diventa un tasto per prenderla.
 *
 * 2026-10-14: il numero di versione fisso NON si mostra piu' (Davide: copriva
 * il tasto «Crea sostituzione» nel pannello Messaggi, e ``1.3.1`` non cambia
 * fra un rilascio e l'altro, quindi non diceva niente). Resta solo il tasto
 * «Versione nuova · ricarica», che compare quando il bundle servito cambia.
 */

import { useEffect, useState } from "react";

export function VersionBadge() {
  const isDev = import.meta.env.DEV;
  const [daRicaricare, setDaRicaricare] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Il bundle da cui gira QUESTA pagina: in produzione e'
    // ``/assets/index-<hash>.js``, e l'hash e' il contenuto.
    const bundleCaricato = import.meta.url.split("/").pop() ?? "";
    const fetchVersion = async () => {
      try {
        const res = await fetch("/health");
        if (!res.ok) return;
        const data = (await res.json()) as { spa_build?: unknown };
        if (cancelled) return;
        // Confronto solo quando entrambi i nomi ci sono: in dev il backend non
        // serve la dist e ``spa_build`` e' vuoto — niente da confrontare, e un
        // falso allarme sarebbe peggio del silenzio.
        const servito = typeof data.spa_build === "string" ? data.spa_build : "";
        if (servito && bundleCaricato && servito !== bundleCaricato) {
          setDaRicaricare(true);
        }
      } catch {
        // Niente network: si riprova al prossimo giro.
      }
    };
    void fetchVersion();
    // Il poll gira SEMPRE, non solo in dev: è in produzione che una scheda
    // resta aperta per giorni. Ogni due minuti, su una GET che non tocca il
    // database.
    const interval = window.setInterval(() => void fetchVersion(), isDev ? 30_000 : 120_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isDev]);

  if (!daRicaricare) return null;

  return (
    <button
      type="button"
      data-testid="version-badge-aggiorna"
      onClick={() => window.location.reload()}
      title="Questa pagina gira su una versione precedente. Ricarica per passare all'ultima."
      className="fixed bottom-2 right-2 z-[9999] select-none font-mono text-[11px] tracking-[-0.01em] tabular-nums bg-amber text-white border border-amber rounded-full px-2.5 py-0.5 shadow-sm hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-[120ms]"
    >
      Versione nuova · ricarica
    </button>
  );
}
